import { getBaseThemeName, getKbConfig, getRiskBandsMode } from "@/features/branding";
import { GeocodingHelpButton } from "@/features/geocoders";
import { InteractiveTutorial, InteractiveTutorialProps } from "@/features/interactive_tutorial";
import { RiskBandsBatchStep1Refs } from "@/tools/risk-bands/components/BatchStep1";
import { RiskBandsBatchStep3Refs } from "@/tools/risk-bands/components/BatchStep3";
import { GeocodeWithResult } from "@/tools/risk-bands/components/MultipleResultsTable";
import { tourSingleResponseAbc, tourSingleResponseHalo } from "@/tools/risk-bands/tutorials/data/single";
import { Alert, Image, TourStepProps } from "antd";
import Link from "next/link";
import { MutableRefObject, useEffect } from "react";
import { useTranslation } from "react-i18next";
export interface QuickBatchScreenState {
  addresses: string;
  results: GeocodeWithResult[];
}
export interface QuickBatchScreenTutorial extends InteractiveTutorialProps<QuickBatchScreenState> {
  refs: {
    back?: MutableRefObject<null>;
    newQuickBatch?: MutableRefObject<null>;
    step1?: RiskBandsBatchStep1Refs;
    step3?: RiskBandsBatchStep3Refs;
  };
}
export const QuickBatchScreenTutorial = ({
  refs,
  ...props
}: QuickBatchScreenTutorial) => {
  // Dummy data used for displaying the tour.
  const tourResponse = {
    abc: tourSingleResponseAbc,
    halo: tourSingleResponseHalo,
    simple: tourSingleResponseHalo
  }[getRiskBandsMode()];
  const {
    t
  } = useTranslation("terms");
  const {
    t: settings
  } = useTranslation("tools", {
    keyPrefix: "settings"
  });
  const tutorialState: QuickBatchScreenState = {
    addresses: "Disneyland Hong Kong, Lantau Island, Hong Kong",
    results: [{
      geocode: {
        confidence: 100,
        input: "Disneyland Hong Kong, Lantau Island, Hong Kong",
        latLng: {
          lat: 22.3129666,
          lng: 114.0412819
        },
        name: "",
        quality: "Good",
        source: "google"
      },
      result: tourResponse
    }]
  };
  useEffect(() => {
    if (props.open && props.setState) {
      props.setState(tutorialState);
    }
  }, [props.open]);
  const riskBandLetters = getBaseThemeName() === "hkma" ? "ABCDE" : "ABC";
  const steps: TourStepProps[] = [{
    title: "",
    description: <>
          Click on &lsquo;<b>Quick Batch Screen</b>&rsquo; from the main menu
          {getBaseThemeName() === "hkma" && <Image width={480} alt="Indication to click Quick Batch Screen from the main menu" src="/img/tours/batch/batch_nav.jpg" />}
        </>
  }, {
    title: "",
    description: <>
          <p>
            Under &lsquo;
            <b>
              <b>Enter Addresses</b>
            </b>
            &rsquo;, type or paste add up to 30 address locations, each on a single line.
          </p>
          <Alert className="mb-4 mt-2" description={<>
                Please enter addresses in the format specified by local post authorities. Do not enter unit numbers,
                suite numbers or floor numbers. Avoid business names or landmarks. For more information, read the{" "}
                <GeocodingHelpButton isLink>geocoding guide</GeocodingHelpButton> here.
              </>} showIcon type="error" />
        </>,
    placement: "center",
    target: () => refs.step1?.addresses.current || null
  }, {
    title: "",
    description: getKbConfig().glossary.enabled && <>
          <p>
            Under &lsquo;<b>Settings</b>&rsquo;, choose an asset type from the &lsquo;<b>Asset Type (Archetype)</b>
            &rsquo; selection drop-down menu. For more information on selecting archetypes, see &lsquo;
            <Link href="/archetypes">Glossary &ndash; Archetype</Link>
            &rsquo; .
          </p>
        </>,
    target: () => refs.step1?.advancedSettings?.archetype.current || null
  }, {
    title: "",
    description: <>
          <p>
            Under &lsquo;<b>Settings</b>&rsquo;, enter an asset &lsquo;<b>Build Year</b>&rsquo;. If unsure, leave the
            &lsquo;<b>Default for Asset Type</b>&rsquo; value in the field, or select &lsquo;<b>Custom Value</b>&rsquo;
            to enter the exact/estimated &lsquo;<b>Build Year</b>&rsquo; if known.
          </p>
        </>,
    target: () => refs.step1?.advancedSettings?.buildYear.current || null
  }, {
    title: "",
    description: <>
          <p>
            Under &lsquo;<b>Settings</b>&rsquo;, enter the asset&apos;s &lsquo;<b>Floor Height Above Ground</b>&rsquo;.
            You can estimate this by selecting a foundation type from the drop-down menu, or choose &lsquo;
            <b>Custom Value</b>&rsquo; to enter a precise height value (metres).
          </p>
        </>,
    target: () => refs.step1?.advancedSettings?.heightAboveGround.current || null
  }, {
    title: "",
    description: <>
          Under &lsquo;<b>{settings("advancedAnalysisSettings")}</b>&rsquo; choose whether to enable or disable{" "}
          {settings("anomalyManagementRp.title")} and {settings("defences.title")}. For more information about{" "}
          {settings("anomalyManagementRp.title")} or {settings("defences.title")},{" "}
          <Link href="/glossary/anomaly-management-1">visit the Glossary</Link>.
        </>,
    target: () => refs.step1?.advancedSettings?.advancedCollapse.current || null
  }, {
    title: "",
    description: <>
          Click &lsquo;<b>Review</b>&rsquo; to open the Review Upload modal.
        </>,
    target: () => refs.step1?.review.current || null
  }, {
    title: "",
    description: <>
          This includes the input settings you&apos;ve selected and the number of analysis run by your organisation to
          date. When ready, click &lsquo;<b>Process</b>
          &rsquo; to begin the analysis.
        </>,
    target: () => {
      return refs.step1?.reviewModal?.proceed?.current || null;
    }
  }, {
    title: "",
    description: <>Assets will populate onto the map.</>,
    target: () => refs.step3?.map?.current || null
  }, {
    title: "",
    description: <>
          Select <b>&lsquo;{t("simpleRiskRatings")}&rsquo;</b> from the drop down to view {riskBandLetters} Ratings for
          each asset.
        </>,
    target: () => refs.step3?.metrics?.current || null
  }];
  if (getRiskBandsMode() !== "abc") {
    steps.push({
      title: "",
      description: <>
          Use the drop-down menu under <b>&lsquo;RCP&rsquo;</b> to compare results between four different RCP scenarios
          – 2.6, 4.5, 6.0 or 8.5.
        </>,
      target: () => refs.step3?.rcp?.current || null
    });
  }
  steps.push(...[{
    title: "",
    description: <>
            Select <b>&lsquo;Flood Exposure&rsquo;</b> from the drop down to view each asset&apos;s exposure to Riverine
            or Surface Water flooding under a 1in1500 return period.
          </>,
    target: () => refs.step3?.metrics?.current || null
  }, {
    title: "",
    description: <>
            Switch between the <b>&lsquo;Map&rsquo;</b> and <b>&lsquo;List&rsquo;</b> view using the toggle on the top
            left.
          </>,
    target: () => refs.step3?.tabToggler?.current || null
  }, {
    title: "",
    description: <>
            When viewing <b>&lsquo;{t("simpleRiskRatings")}&rsquo;</b> in <b>&rsquo;List&rsquo;</b> view - click on the
            arrows next to the years to sort results by ascending or descending risk ratings.
          </>,
    target: () => refs.step3?.table?.current || null
  }, {
    title: "",
    description: <>
            Click <b>&lsquo;Download Results&rsquo;</b> to open the download options. Then select which output file you
            wish to download.
          </>,
    target: () => refs.step3?.downloadResultsTrigger?.current || null
  }, {
    title: "",
    description: <>Then select which output file you wish to download.</>,
    target: () => refs.step3?.downloadResults?.items?.current || null
  }, {
    title: "",
    description: <>
            To go back to edit the asset locations or analysis settings, click &lsquo;<b>Back</b>&rsquo;.
          </>,
    target: () => refs.back?.current || null
  }, {
    title: "",
    description: <>
            To exit results and screen a different address, click &lsquo;
            <b>New Quick Batch</b>
            &rsquo;.
          </>,
    target: () => refs.newQuickBatch?.current || null
  }]);
  return <InteractiveTutorial<QuickBatchScreenState> steps={steps} {...props} data-sentry-element="InteractiveTutorial" data-sentry-component="QuickBatchScreenTutorial" data-sentry-source-file="batch.tsx" />;
};